/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
// https://product-fabric.atlassian.net/browse/NCTD-3682
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import {
  SelectOptionColors,
  lightSelectOptionColors,
  darkSelectOptionColors,
  type SelectOptionColor
} from '@databases/model/field';
import {
  type DatabaseField,
  DatabaseFieldType,
  type RawDatabaseValue,
  type SelectOption
} from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';
import { parse, unparse } from 'papaparse';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { cheerioIterator } from '../../lib/dom';
import { encodeHtml, generateId, isEqualStringSet, trimCss } from '../../utils';
import type { FieldType } from '../field-types';

/** The background and text colours of all select option colours. These are copied from @atlaskit/tag. */
export const selectOptionColorValues: Record<
  SelectOption['color'] | 'undefined',
  [backgroundColor: string, color: string]
> = {
  undefined: [colors.N0, colors.N100],

  blueDark: [colors.B400, colors.N0],
  redDark: [colors.R400, colors.N0],
  yellowDark: [colors.Y300, colors.N800],
  greenDark: [colors.G400, colors.N0],
  tealDark: [colors.T200, colors.N800],
  purpleDark: [colors.P400, colors.N0],
  greyDark: [colors.N500, colors.N0],

  blueLight: [colors.B50, colors.B400],
  redLight: [colors.R50, colors.R400],
  yellowLight: [colors.Y50, '#974F0C'],
  greenLight: [colors.G50, colors.G500],
  tealLight: [colors.T50, colors.N400],
  purpleLight: [colors.P50, colors.P500],
  greyLight: [colors.N40, colors.N500],

  // new colors coming in via ADS refresh
  // (this codepath is only used in case the platform-component-visual-refresh feature gate is off)
  limeDark: ['#B3DF72', colors.N800],
  magentaDark: ['#F797D2', colors.N0],
  orangeDark: ['#FBC828', colors.N800],
  limeLight: ['#94C748', colors.N0],
  magentaLight: ['#FFECF8', colors.N800],
  orangeLight: ['#FCA700', colors.N800]
};

const darkToLightColorMap: Record<string, SelectOptionColor> = {
  blueDark: 'blueLight',
  redDark: 'redLight',
  yellowDark: 'yellowLight',
  greenDark: 'greenLight',
  tealDark: 'tealLight',
  purpleDark: 'purpleLight',
  greyDark: 'greyLight',
  limeDark: 'limeLight',
  magentaDark: 'magentaLight',
  orangeDark: 'orangeLight'
};

export const mapColorToNewPalette = (color: SelectOptionColor): string => {
  // remove dark from color name
  const newColor = color.replace('Dark', '');
  return newColor;
};

export const mapDarkColorToLight = (color: SelectOptionColor): SelectOptionColor => {
  return darkToLightColorMap[color] ?? color;
};

// maps tag color to icon tile appearance for group header
export const mapColorToIconTileAppearance = (
  color?: string
):
  | 'gray'
  | 'blue'
  | 'teal'
  | 'green'
  | 'lime'
  | 'yellow'
  | 'orange'
  | 'red'
  | 'magenta'
  | 'purple' => {
  if (!color) {
    return 'gray';
  }

  const effectiveColor = isFeatureFlagEnabled('platformLozengeTagBadgeVisualUplifts')
    ? mapDarkColorToLight(color as SelectOptionColor)
    : color;
  const mappedColor = mapColorToNewPalette(effectiveColor);

  switch (mappedColor) {
    case 'greyLight':
    case 'grey':
      return 'gray';
    case 'blueLight':
    case 'blue':
      return 'blue';
    case 'tealLight':
    case 'teal':
      return 'teal';
    case 'greenLight':
    case 'green':
      return 'green';
    case 'limeLight':
    case 'lime':
      return 'lime';
    case 'yellowLight':
    case 'yellow':
      return 'yellow';
    case 'orangeLight':
    case 'orange':
      return 'orange';
    case 'redLight':
    case 'red':
      return 'red';
    case 'magentaLight':
    case 'magenta':
      return 'magenta';
    case 'purpleLight':
    case 'purple':
      return 'purple';
    default:
      return 'gray';
  }
};

// maps tag color to badge for group header
export const mapColorToBadgeToken = (color?: string): string => {
  if (!color) {
    return 'color.background.accent.gray.subtler';
  }

  const effectiveColor = isFeatureFlagEnabled('platformLozengeTagBadgeVisualUplifts')
    ? mapDarkColorToLight(color as SelectOptionColor)
    : color;
  const mappedColor = mapColorToNewPalette(effectiveColor);

  switch (mappedColor) {
    case 'greyLight':
    case 'grey':
      return token('color.background.accent.gray.subtler');
    case 'blueLight':
    case 'blue':
      return token('color.background.accent.blue.subtler');
    case 'tealLight':
    case 'teal':
      return token('color.background.accent.teal.subtler');
    case 'greenLight':
    case 'green':
      return token('color.background.accent.green.subtler');
    case 'limeLight':
    case 'lime':
      return token('color.background.accent.lime.subtler');
    case 'yellowLight':
    case 'yellow':
      return token('color.background.accent.yellow.subtler');
    case 'orangeLight':
    case 'orange':
      return token('color.background.accent.orange.subtler');
    case 'redLight':
    case 'red':
      return token('color.background.accent.red.subtler');
    case 'magentaLight':
    case 'magenta':
      return token('color.background.accent.magenta.subtler');
    case 'purpleLight':
    case 'purple':
      return token('color.background.accent.purple.subtler');
    default:
      return token('color.background.neutral.subtle');
  }
};

export function convertStringToTags(value: string): string[] {
  if (value.startsWith('"') && value.endsWith('"')) {
    return [value.slice(1, -1)];
  }
  return value.split(/,\s*/);
}

function getSelectedOptions({
  field,
  value
}: {
  field: DatabaseField;
  value: RawDatabaseValue<DatabaseFieldType.SELECT>;
}): SelectOption[] {
  return field.settings.selectOptions.filter((op) => value.includes(op.id ?? op.label));
}

function getStringRepresentation(context: {
  field: DatabaseField;
  value: RawDatabaseValue<DatabaseFieldType.SELECT>;
}): string {
  return getSelectedOptions(context)
    .map((op) => op.label)
    .join('\n');
}

function getNumberRepresentation(context: {
  field: DatabaseField;
  value: RawDatabaseValue<DatabaseFieldType.SELECT>;
}): string {
  const stringValue = getStringRepresentation(context);
  return isNaN(Number(stringValue)) ? '' : stringValue;
}

export function getNewColorSuggestions(options: SelectOption[]): SelectOption['color'][] {
  const usedTimes = Object.fromEntries(
    SelectOptionColors.map((c) => [c, options.filter((v) => v.color === c).length])
  ) as Record<SelectOption['color'], number>;

  const isUsingSomeLightColors = lightSelectOptionColors.some((c) => usedTimes[c] > 0);
  const isUsingAllLightColors = lightSelectOptionColors.every((c) => usedTimes[c] > 0);
  const isUsingSomeDarkColors = darkSelectOptionColors.some((c) => usedTimes[c] > 0);
  const isUsingAllDarkColors = darkSelectOptionColors.every((c) => usedTimes[c] > 0);

  // If we are using only dark colors so far, suggest only dark colors (unless we are already using all of them).
  // If we are using only light colors so far, suggest only light colors (unless we are alreay using all of them).
  // If we are using a mix of dark and light colors, suggest any colors.
  // If there are no options yet, suggest only light colors
  const colorRange = isUsingSomeDarkColors
    ? isUsingAllDarkColors || isUsingSomeLightColors
      ? SelectOptionColors
      : darkSelectOptionColors
    : isUsingAllLightColors
      ? SelectOptionColors
      : lightSelectOptionColors;

  const minUsedTimes = Math.min(
    ...Object.entries(usedTimes).flatMap(([c, u]) =>
      colorRange.includes(c as any) ? [u] : []
    )
  );
  return colorRange.filter((c) => usedTimes[c] === minUsedTimes);
}

export function pickNewColor(options: SelectOption[]): SelectOption['color'] {
  const suggestions = getNewColorSuggestions(options);
  return suggestions[Math.floor(Math.random() * suggestions.length)];
}

export function getNewOption(options: SelectOption[]): SelectOption {
  return {
    id: generateId(),
    color: pickNewColor(options),
    label: ''
  };
}

export function getSelectOptionTag(op: SelectOption | undefined): string {
  const background = selectOptionColorValues[op ? op.color : 'undefined'][0];
  const color = selectOptionColorValues[op ? op.color : 'undefined'][1];
  const label = op ? op.label : 'Undefined';
  const className = 'nct-databases-orderly-select-option-tag';
  const style = trimCss(`
        display: inline-block;
        padding: 0px 4px;
        border-radius: 3px;
        background: ${background};
        color: ${color};
        font-size: 11px;
        line-height: 16px;
        font-weight: bold;
        text-transform: uppercase;
    `);
  return `<span class="${className} ${className}-${
    op?.color ?? 'undefined'
  }" style="${style}">${encodeHtml(label)}</span>`;
}

/**
 * Given an array of tags, finds the matching tag in the select options for each
 * tag. Returns the ID of the matching tag, or if no match is found returns the
 * tag itself or nothing depending on the value of returnMatchingTags.
 * @param tags tags to match
 * @param selectOptions the select options to match against
 * @param returnNonMatchingTags if true, returns the tag itself if no match is
 * found, otherwise returns nothing
 */
export function findMatchingTags(
  tags: string[],
  selectOptions: SelectOption[],
  returnNonMatchingTags: boolean
): string[] {
  return tags
    .map((tag) => {
      const matchingOption = selectOptions.find((op) => {
        return tag === op.label.trim();
      });

      // TODO NCTD-2806: remove returnNonMatchingTags
      return matchingOption
        ? (matchingOption.id ?? matchingOption.label)
        : returnNonMatchingTags
          ? tag
          : null;
    })
    .filter((match) => match !== null);
}

const selectFieldType: FieldType<DatabaseFieldType.SELECT> = {
  supportedFilterTypes: [
    DatabaseFilterType.EQUALS,
    DatabaseFilterType.CONTAINS,
    DatabaseFilterType.EMPTY
  ],

  isCsvImportSupported: true,

  renderExportView: (context) => {
    return getSelectedOptions(context)
      .map((op) => getSelectOptionTag(op))
      .join(' ');
  },

  parseFromHtml: (el, context) => {
    let elements = el.children().eq(0);

    // When the clipboard contains copied select cells, the content will be wrapped in a <span>, and we need
    // to extract the inner content. However, we only want to do this for select cells. For example, if the clipboard
    // contains a copied text cell, it is not wrapped in an outer span, so the extraction would yield nothing.
    if (elements.hasClass('nct-databases-value-select')) {
      elements = elements.find('span');
    }
    const encodedText = [...cheerioIterator(elements)]
      .map((element) => element.text().trim())
      .filter((text) => text !== '');

    return findMatchingTags(encodedText, context.field.settings.selectOptions, true);
  },

  getInitialValue: () => [],

  isEmptyValue: (context) => getSelectedOptions(context).length === 0,

  isEqualValue: (
    { value: value1, field: field1, database: database1 },
    { value: value2, field: field2, database: database2 }
  ) =>
    isEqualStringSet(
      value1.filter((value) =>
        database1.fieldsById[field1.id].settings.selectOptions.find(
          (option) => option.id === value || option.label === value
        )
      ),
      value2.filter((value) =>
        database2.fieldsById[field2.id].settings.selectOptions.find(
          (option) => option.id === value || option.label === value
        )
      )
    ),

  getStringRepresentation,

  getCsvRepresentation: (context: {
    field: DatabaseField;
    value: RawDatabaseValue<DatabaseFieldType.SELECT>;
  }) => {
    const options = getSelectedOptions(context).map((op) => op.label);
    return unparse([options]);
  },

  convertFromCsv: (context) => {
    const tags =
      parse<string[]>(context.value.plain, {
        transform: (value) => value.trim()
      }).data[0] ?? [];

    return findMatchingTags(tags, context.field.settings.selectOptions, false);
  },

  convertFrom: {
    [DatabaseFieldType.TEXT]: (context) => {
      const trimmed = context.value.plain.trim();
      if (!trimmed) {
        return [];
      }
      const tags = convertStringToTags(trimmed);
      return findMatchingTags(tags, context.field.settings.selectOptions, true);
    }
  },

  convertTo: {
    [DatabaseFieldType.TEXT]: (context) => ({
      plain: getStringRepresentation(context)
    }),
    [DatabaseFieldType.NUMBER]: getNumberRepresentation
  }
};

export default selectFieldType;
